import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import {
  Plus, Eye, EyeOff, Loader2, CheckCircle, Package, Trash2,
  AlertTriangle, Lock, Calendar, Handshake, Clock, Bell, ScanLine, Copy, QrCode
} from 'lucide-react';
import { SearchableSelect, MEXICAN_STATES, COUNTRIES } from '../SearchableSelect';
import ClientSuggestion from '../ClientSuggestion';

const CreateMovementModal = ({
  isOpen, setIsOpen,
  form, setForm,
  newItem, setNewItem,
  saving, handleSave,
  addItem, removeItem,
  availableAtLocation, loadAvailableEquipment,
  filteredAvailableEquipment,
  categoriesWithAvailability,
  reservedUnitSerials,
  showResponsiblePassword, setShowResponsiblePassword,
  showAdminPassword, setShowAdminPassword,
  responsibles, clients, locations, categories, equipment,
  systemUsers = [],
  locationsWithAvailability,
  showScanner, setShowScanner,
  scannerConfig, scanMode, setScanMode, scanBatchMode, setScanBatchMode,
  handleScanSuccess, hasPermission,
  duplicateInfo,
  searchBySerial = true,
  setSearchBySerial,
  searchByReceptor = false,
  setSearchByReceptor
}) => {

  const isDuplicate = !!duplicateInfo;

  const renderClientSubSelectors = () => {
    if (!form.client_id) return null;
    const client = clients.find(c => c.id === form.client_id);
    if (!client) return null;
    const allContacts = [
      ...(client.contact_name ? [{ name: client.contact_name, email: client.email, phone: client.phone, city: client.city, state: client.state, country: client.country }] : []),
      ...(client.contacts || [])
    ];
    const uniqueContacts = [...new Set(allContacts.map(c => c.name).filter(Boolean))];
    const uniqueCities = [...new Set([client.city, ...allContacts.map(c => c.city)].filter(Boolean))];
    const uniqueStates = [...new Set([client.state, ...allContacts.map(c => c.state)].filter(Boolean))];
    const uniqueCountries = [...new Set([client.country, ...allContacts.map(c => c.country)].filter(Boolean))];
    const hasMultiple = uniqueContacts.length > 1 || uniqueCities.length > 1 || uniqueStates.length > 1 || uniqueCountries.length > 1;
    if (!hasMultiple) return null;
    return (
      <div className="p-3 border rounded-lg bg-blue-500/5 border-blue-500/20 space-y-2">
        <p className="text-xs font-medium text-blue-500">Datos del cliente (seleccionar):</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {uniqueContacts.length > 1 && (
            <div className="space-y-1">
              <Label className="text-xs">Contacto</Label>
              <Select value={form.client_contact || ''} onValueChange={v => setForm({...form, client_contact: v})}>
                <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Contacto" /></SelectTrigger>
                <SelectContent>
                  {uniqueContacts.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {uniqueCities.length > 1 && (
            <div className="space-y-1">
              <Label className="text-xs">Ciudad</Label>
              <Select value={form.event_city || ''} onValueChange={v => setForm({...form, event_city: v})}>
                <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Ciudad" /></SelectTrigger>
                <SelectContent>
                  {uniqueCities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {uniqueStates.length > 1 && (
            <div className="space-y-1">
              <Label className="text-xs">Estado</Label>
              <Select value={form.event_state || ''} onValueChange={v => setForm({...form, event_state: v})}>
                <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent>
                  {uniqueStates.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {uniqueCountries.length > 1 && (
            <div className="space-y-1">
              <Label className="text-xs">Pais</Label>
              <Select value={form.event_country || ''} onValueChange={v => setForm({...form, event_country: v})}>
                <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Pais" /></SelectTrigger>
                <SelectContent>
                  {uniqueCountries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[750px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isDuplicate ? (
                <span className="flex items-center gap-2">
                  <Copy className="h-5 w-5 text-amber-500" />
                  Duplicar Movimiento
                </span>
              ) : (
                form.movement_subtype === 'evento' ? 'Nueva Salida para Evento' : 'Nuevo Préstamo de Equipo'
              )}
            </DialogTitle>
            <DialogDescription>
              {isDuplicate
                ? 'Revisa y confirma los datos antes de registrar este movimiento duplicado'
                : (form.movement_subtype === 'evento' 
                    ? 'Registra una salida de equipo para un evento'
                    : 'Registra un préstamo de equipo a largo plazo')
              }
            </DialogDescription>
          </DialogHeader>

          {/* Duplicate Warning Banner */}
          {isDuplicate && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm" data-testid="duplicate-warning">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-amber-500">Movimiento Duplicado</p>
                  <p className="text-muted-foreground text-xs mt-1">
                    Este movimiento es una copia de: <strong>{duplicateInfo.original_event}</strong> (ID: {duplicateInfo.original_id?.slice(0, 8)}).
                    Verifica que los datos y equipos sean correctos antes de registrar. Se guardará como movimiento duplicado en el sistema.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="grid gap-4 py-4">
            {/* Movement Type Selector */}
            <div className="flex gap-2 p-1 bg-muted rounded-lg">
              <Button
                type="button"
                variant={form.movement_subtype === 'evento' ? 'default' : 'ghost'}
                className={`flex-1 ${form.movement_subtype === 'evento' ? 'bg-primary hover:bg-primary/90' : ''}`}
                onClick={() => setForm({ ...form, movement_subtype: 'evento', type: 'salida' })}
              >
                <Calendar className="h-4 w-4 mr-2" />
                Evento
              </Button>
              <Button
                type="button"
                variant={form.movement_subtype === 'prestamo' ? 'default' : 'ghost'}
                className={`flex-1 ${form.movement_subtype === 'prestamo' ? 'bg-purple-600 hover:bg-purple-600/90' : ''}`}
                onClick={() => setForm({ ...form, movement_subtype: 'prestamo', type: 'prestamo' })}
              >
                <Handshake className="h-4 w-4 mr-2" />
                Préstamo de Equipo
              </Button>
            </div>

            {/* Event Form */}
            {form.movement_subtype === 'evento' && (
              <Card className="bg-primary/5 border-primary/20">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm text-primary">Información del Evento</CardTitle>
                </CardHeader>
                <CardContent className="pb-4 space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label>Fecha del Evento *</Label>
                      <Input
                        type="date"
                        value={form.event_date}
                        onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                        data-testid="event-date-input"
                        className="[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:hover:opacity-100"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Hora del Evento</Label>
                      <Input
                        type="time"
                        value={form.event_time}
                        onChange={(e) => setForm({ ...form, event_time: e.target.value })}
                        data-testid="event-time-input"
                        className="[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:hover:opacity-100"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>No. Control *</Label>
                      <Input
                        value={form.control_number}
                        onChange={(e) => {
                          const onlyDigits = e.target.value.replace(/\D/g, '').slice(0, 5);
                          setForm({ ...form, control_number: onlyDigits });
                        }}
                        placeholder="12345"
                        inputMode="numeric"
                        pattern="\d{5}"
                        maxLength={5}
                        data-testid="control-number-input"
                        className={form.control_number && form.control_number.length !== 5 ? 'border-red-500 focus-visible:ring-red-500' : ''}
                      />
                      <p className="text-[10px] text-muted-foreground">
                        Exactamente 5 dígitos numéricos
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Nombre del Evento/Destino</Label>
                    <Input
                      value={form.event_name}
                      onChange={(e) => setForm({ ...form, event_name: e.target.value })}
                      placeholder="Ej: Congreso Internacional, Capacitación..."
                      data-testid="event-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2 relative">
                      <Label>Nombre del Cliente *</Label>
                      <Input
                        value={form.client_name}
                        onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                        placeholder="Nombre completo del cliente"
                        data-testid="client-name-input"
                        autoComplete="off"
                      />
                      {!form.client_id && (
                        <ClientSuggestion
                          inputValue={form.client_name}
                          clients={clients}
                          onSelect={(client) => {
                            setForm({
                              ...form,
                              client_id: client.id,
                              client_name: client.name,
                              client_contact: client.contact_name || form.client_contact,
                              event_city: client.city || form.event_city,
                              event_state: client.state || form.event_state,
                              event_country: client.country || form.event_country,
                            });
                          }}
                        />
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Nombre de Contacto</Label>
                      <Input
                        value={form.client_contact}
                        onChange={(e) => setForm({ ...form, client_contact: e.target.value })}
                        placeholder="Nombre del contacto"
                        data-testid="client-contact-input"
                      />
                    </div>
                  </div>

                  {/* Client Quick Select */}
                  {clients.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Seleccionar cliente existente (opcional)</Label>
                      <Select
                        value={form.client_id || ''}
                        onValueChange={(clientId) => {
                          if (clientId === 'none') {
                            setForm({ ...form, client_id: '' });
                            return;
                          }
                          const client = clients.find(c => c.id === clientId);
                          if (client) {
                            setForm({
                              ...form,
                              client_id: clientId,
                              client_name: client.name,
                              client_contact: client.contact_name || form.client_contact,
                              event_city: client.city || form.event_city,
                              event_state: client.state || form.event_state,
                              event_country: client.country || form.event_country,
                            });
                          }
                        }}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid="client-select">
                          <SelectValue placeholder="Vincular a cliente registrado..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin vincular</SelectItem>
                          {clients.map(c => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.name}{c.company ? ` (${c.company})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Client Data Sub-Selectors */}
                  {renderClientSubSelectors()}

                  {/* City, State, Country */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs">Ciudad</Label>
                      <Input
                        value={form.event_city}
                        onChange={(e) => setForm({ ...form, event_city: e.target.value })}
                        placeholder="Ciudad"
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Estado</Label>
                      <SearchableSelect
                        value={form.event_state}
                        onValueChange={(v) => setForm({ ...form, event_state: v })}
                        options={MEXICAN_STATES}
                        placeholder="Estado"
                        showManualOption={true}
                        manualLabel="Agregar manualmente"
                        size="sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Pais</Label>
                      <SearchableSelect
                        value={form.event_country}
                        onValueChange={(v) => setForm({ ...form, event_country: v })}
                        options={COUNTRIES}
                        placeholder="Pais"
                        showManualOption={true}
                        manualLabel="Agregar manualmente"
                        size="sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Notas del Evento</Label>
                    <Textarea
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Información adicional..."
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Loan Form */}
            {form.movement_subtype === 'prestamo' && (
              <Card className="bg-purple-500/5 border-purple-500/20">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm text-purple-500 flex items-center gap-2">
                    <Handshake className="h-4 w-4" />
                    Información del Préstamo
                  </CardTitle>
                </CardHeader>
                <CardContent className="pb-4 space-y-4">
                  <div className="space-y-2">
                    <Label>Motivo del Préstamo *</Label>
                    <Textarea
                      value={form.loan_reason}
                      onChange={(e) => setForm({ ...form, loan_reason: e.target.value })}
                      placeholder="Ej: Trabajo remoto, Capacitación, Proyecto especial..."
                      rows={2}
                    />
                  </div>
                  
                  <div className="space-y-3">
                    <Label>Duración del Préstamo</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { value: 1, unit: 'days', label: '1 Día' },
                        { value: 7, unit: 'days', label: '1 Semana' },
                        { value: 15, unit: 'days', label: '15 Días' },
                        { value: 1, unit: 'months', label: '1 Mes' },
                      ].map((preset) => (
                        <Button
                          key={`${preset.value}-${preset.unit}`}
                          type="button"
                          variant={form.loan_duration_value === String(preset.value) && form.loan_duration_unit === preset.unit ? 'default' : 'outline'}
                          size="sm"
                          className={form.loan_duration_value === String(preset.value) && form.loan_duration_unit === preset.unit ? 'bg-purple-600 hover:bg-purple-700' : ''}
                          onClick={() => setForm({ 
                            ...form, 
                            loan_duration_value: String(preset.value), 
                            loan_duration_unit: preset.unit,
                            loan_indefinite: false 
                          })}
                        >
                          {preset.label}
                        </Button>
                      ))}
                    </div>
                    
                    <div className="flex items-center gap-4 p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="loan_indefinite"
                          checked={form.loan_indefinite}
                          onCheckedChange={(checked) => setForm({ ...form, loan_indefinite: checked, loan_duration_value: '' })}
                        />
                        <Label htmlFor="loan_indefinite" className="font-normal cursor-pointer text-sm">
                          Sin tiempo determinado
                        </Label>
                      </div>
                      
                      {!form.loan_indefinite && (
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-sm text-muted-foreground">o personalizar:</span>
                          <Input
                            type="number"
                            min="1"
                            value={form.loan_duration_value}
                            onChange={(e) => setForm({ ...form, loan_duration_value: e.target.value })}
                            placeholder="Cantidad"
                            className="w-20"
                          />
                          <Select
                            value={form.loan_duration_unit}
                            onValueChange={(value) => setForm({ ...form, loan_duration_unit: value })}
                          >
                            <SelectTrigger className="w-28">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="hours">Horas</SelectItem>
                              <SelectItem value="days">Días</SelectItem>
                              <SelectItem value="months">Meses</SelectItem>
                              <SelectItem value="years">Años</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                    
                    {!form.loan_indefinite && form.loan_duration_value && (
                      <div className="text-sm text-purple-400 flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Fecha estimada de devolución: {(() => {
                          const now = new Date();
                          const val = parseInt(form.loan_duration_value) || 0;
                          if (form.loan_duration_unit === 'hours') now.setHours(now.getHours() + val);
                          else if (form.loan_duration_unit === 'days') now.setDate(now.getDate() + val);
                          else if (form.loan_duration_unit === 'months') now.setMonth(now.getMonth() + val);
                          else if (form.loan_duration_unit === 'years') now.setFullYear(now.getFullYear() + val);
                          return now.toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                        })()}
                      </div>
                    )}
                  </div>
                  
                  {!form.loan_indefinite && form.loan_duration_value && (
                    <div className="space-y-2 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="loan_reminders"
                          checked={form.loan_reminders_enabled}
                          onCheckedChange={(checked) => setForm({ ...form, loan_reminders_enabled: checked })}
                        />
                        <Label htmlFor="loan_reminders" className="font-normal cursor-pointer flex items-center gap-2">
                          <Bell className="h-4 w-4 text-amber-500" />
                          Activar recordatorios de vencimiento
                        </Label>
                      </div>
                      {form.loan_reminders_enabled && (
                        <div className="mt-2 text-sm text-muted-foreground">
                          Se enviará notificación por email y en el sistema a los <strong>3 y 7 días</strong> antes del vencimiento.
                        </div>
                      )}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Notas adicionales</Label>
                    <Textarea
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Información adicional sobre el préstamo..."
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Responsable Selection */}
            <div className="space-y-2">
              <Label>Responsable (quien entrega) *</Label>
              <Select
                value={form.responsible_id}
                onValueChange={(value) => setForm({ ...form, responsible_id: value })}
              >
                <SelectTrigger data-testid="movement-responsible-select">
                  <SelectValue placeholder="Selecciona responsable" />
                </SelectTrigger>
                <SelectContent>
                  {responsibles.map(resp => (
                    <SelectItem key={resp.id} value={resp.id}>{resp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Authentication */}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Contraseña del Responsable *</Label>
                <div className="relative">
                  <Input
                    type={showResponsiblePassword ? "text" : "password"}
                    value={form.user_password}
                    onChange={(e) => setForm({ ...form, user_password: e.target.value })}
                    placeholder="Contraseña del responsable"
                    data-testid="responsible-password-input"
                    disabled={form.admin_override}
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowResponsiblePassword(!showResponsiblePassword)}
                    data-testid="toggle-responsible-password"
                  >
                    {showResponsiblePassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  El responsable debe confirmar su identidad con su contraseña
                </p>
              </div>

              {/* v5.5.0 Fase 2 — Responsable del equipo (quien recibe) = usuario del sistema (debajo de la contraseña de quien entrega) */}
              {form.type === 'salida' && form.movement_subtype === 'evento' && (
                <div className="space-y-2 pt-3 border-t border-border/30">
                  <Label>Responsable del equipo (quien recibe)</Label>
                  <Select
                    value={form.receiver_user_id || ''}
                    onValueChange={(value) => setForm({ ...form, receiver_user_id: value === '__none__' ? '' : value })}
                  >
                    <SelectTrigger data-testid="movement-receiver-select">
                      <SelectValue placeholder="Selecciona quién recibe (usuario)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Sin confirmación de receptor —</SelectItem>
                      {systemUsers.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name || u.username}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.receiver_user_id && (
                    <p className="text-xs text-muted-foreground">
                      La salida quedará <strong>pendiente de confirmación</strong>. Se notificará a quien recibe para que confirme con su usuario y contraseña.
                    </p>
                  )}
                </div>
              )}
              
              <div className="pt-2 border-t border-border/30">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, admin_override: !form.admin_override, user_password: '' })}
                  className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                >
                  <Lock className="h-3 w-3" />
                  {form.admin_override ? '← Usar contraseña del responsable' : '¿No tiene contraseña? Override de admin →'}
                </button>
                
                {form.admin_override && (
                  <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg space-y-3">
                    <p className="text-xs text-amber-400 flex items-center gap-1">
                      <Lock className="h-3 w-3" />
                      Override de Administrador activo
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-xs">Usuario Admin *</Label>
                        <Input
                          value={form.admin_username}
                          onChange={(e) => setForm({ ...form, admin_username: e.target.value })}
                          placeholder="Usuario"
                          data-testid="admin-username-input"
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Contraseña Admin *</Label>
                        <div className="relative">
                          <Input
                            type={showAdminPassword ? "text" : "password"}
                            value={form.admin_password}
                            onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                            placeholder="Contraseña"
                            data-testid="admin-password-input"
                            className="h-8 pr-10"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-0 top-0 h-full px-2 hover:bg-transparent"
                            onClick={() => setShowAdminPassword(!showAdminPassword)}
                            data-testid="toggle-admin-password"
                          >
                            {showAdminPassword ? <EyeOff className="h-3 w-3 text-muted-foreground" /> : <Eye className="h-3 w-3 text-muted-foreground" />}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Notes (only for non-event non-loan, since event/loan have their own notes) */}
            {form.movement_subtype !== 'evento' && form.movement_subtype !== 'prestamo' && (
              <div className="space-y-2">
                <Label>Notas / Observaciones</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Notas adicionales..."
                  rows={2}
                />
              </div>
            )}

            {/* Add Item */}
            <Card className="bg-muted/30">
              <CardHeader className="py-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Agregar Equipo</CardTitle>
                  {scannerConfig.enabled && hasPermission('use_barcode_scanner') && (
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setScanMode('equipment');
                          setScanBatchMode(false);
                          setShowScanner(true);
                        }}
                        data-testid="open-scanner-btn"
                      >
                        <ScanLine className="h-4 w-4 mr-2" />
                        Escanear
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setScanMode('any');
                          setScanBatchMode(true);
                          setShowScanner(true);
                        }}
                        data-testid="open-batch-scanner-btn"
                        title="Escaneo múltiple"
                      >
                        <ScanLine className="h-4 w-4 mr-1" />
                        Múltiple
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pb-4 space-y-3">
                {scannerConfig.enabled && scannerConfig.scan_locations && hasPermission('use_barcode_scanner') && (
                  <div className="flex gap-2 items-center mb-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setScanMode('location');
                        setShowScanner(true);
                      }}
                      className="text-xs"
                    >
                      <ScanLine className="h-3 w-3 mr-1" />
                      Escanear ubicación
                    </Button>
                    <span className="text-xs text-muted-foreground">o selecciona manualmente:</span>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">1. Ubicación Origen *</Label>
                    <Select
                      value={newItem.location_id}
                      onValueChange={(value) => {
                        setNewItem({ ...newItem, location_id: value, category_id: '', equipment_id: '', quantity: 1, selected_units: [] });
                        loadAvailableEquipment(value);
                      }}
                    >
                      <SelectTrigger data-testid="location-select">
                        <SelectValue placeholder="Selecciona ubicación" />
                      </SelectTrigger>
                      <SelectContent>
                        {locationsWithAvailability.map(loc => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {loc.name} ({loc.available_count} disp.)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">2. Categoría (opcional)</Label>
                    <Select
                      value={newItem.category_id || 'all'}
                      onValueChange={(value) => setNewItem({ ...newItem, category_id: value === 'all' ? '' : value, equipment_id: '', quantity: 1, selected_units: [] })}
                      disabled={!newItem.location_id}
                    >
                      <SelectTrigger data-testid="category-select">
                        <SelectValue placeholder="Todas las categorías" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas las categorías</SelectItem>
                        {categoriesWithAvailability.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name} ({cat.available_count} disp.)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">3. Equipo *</Label>
                    <Select
                      value={newItem.equipment_id}
                      onValueChange={(value) => setNewItem({ ...newItem, equipment_id: value, quantity: 1, selected_units: [] })}
                      disabled={!newItem.location_id}
                    >
                      <SelectTrigger data-testid="equipment-select">
                        <SelectValue placeholder={newItem.location_id ? "Selecciona equipo" : "Primero selecciona ubicación"} />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredAvailableEquipment.map(eq => (
                          <SelectItem key={eq.equipment_id} value={eq.equipment_id}>
                            {eq.equipment_name} (Disp: {eq.available_count})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Serial/Receptor number selection */}
                {newItem.equipment_id && filteredAvailableEquipment.find(e => e.equipment_id === newItem.equipment_id)?.units?.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Seleccionar unidades específicas (opcional)</Label>
                      <div className="flex gap-1">
                        <Button type="button" variant="ghost" size="sm" className="h-6 text-xs px-2"
                          onClick={() => {
                            const allUnits = filteredAvailableEquipment.find(e => e.equipment_id === newItem.equipment_id)?.units
                              ?.filter(unit => {
                                if (reservedUnitSerials.has(unit.serial_number)) return false;
                                if (!newItem.serial_search) return true;
                                const q = newItem.serial_search.toLowerCase();
                                const matchSerial = searchBySerial && unit.serial_number?.toLowerCase().includes(q);
                                const matchReceptor = searchByReceptor && unit.receptor_number?.toLowerCase().includes(q);
                                return matchSerial || matchReceptor || (!searchBySerial && !searchByReceptor);
                              }) || [];
                            setNewItem({ ...newItem, selected_units: allUnits, quantity: allUnits.length });
                          }}>Seleccionar todos</Button>
                        <Button type="button" variant="ghost" size="sm" className="h-6 text-xs px-2"
                          onClick={() => setNewItem({ ...newItem, selected_units: [], quantity: 1 })}>Limpiar</Button>
                      </div>
                    </div>
                    {/* Search mode toggles */}
                    <div className="flex items-center gap-4 px-1">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <Checkbox
                          checked={searchBySerial}
                          onCheckedChange={(checked) => setSearchBySerial(checked)}
                          data-testid="search-by-serial-toggle"
                        />
                        <span className="text-xs text-muted-foreground">Buscar por No. de Serie</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <Checkbox
                          checked={searchByReceptor}
                          onCheckedChange={(checked) => setSearchByReceptor(checked)}
                          data-testid="search-by-receptor-toggle"
                        />
                        <span className="text-xs text-muted-foreground">Buscar por No. de Equipo</span>
                      </label>
                    </div>
                    <Input
                      placeholder={searchByReceptor && !searchBySerial ? "Buscar por número de equipo..." : searchBySerial && !searchByReceptor ? "Buscar por número de serie..." : "Buscar por serie o equipo..."}
                      value={newItem.serial_search || ''}
                      onChange={(e) => setNewItem({ ...newItem, serial_search: e.target.value })}
                      className="h-8 text-xs"
                      data-testid="unit-search-input"
                    />
                    <div className="border rounded-md p-2 max-h-32 overflow-y-auto">
                      <div className="grid grid-cols-2 gap-2">
                        {filteredAvailableEquipment.find(e => e.equipment_id === newItem.equipment_id)?.units
                          ?.filter(unit => {
                            if (reservedUnitSerials.has(unit.serial_number)) return false;
                            if (!newItem.serial_search) return true;
                            const q = newItem.serial_search.toLowerCase();
                            const matchSerial = searchBySerial && unit.serial_number?.toLowerCase().includes(q);
                            const matchReceptor = searchByReceptor && unit.receptor_number?.toLowerCase().includes(q);
                            return matchSerial || matchReceptor || (!searchBySerial && !searchByReceptor);
                          })
                          .map(unit => (
                          <label key={unit.id} className="flex items-center gap-2 p-1 rounded hover:bg-muted/50 cursor-pointer">
                            <Checkbox
                              checked={newItem.selected_units.some(u => u.id === unit.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setNewItem({ ...newItem, selected_units: [...newItem.selected_units, unit], quantity: newItem.selected_units.length + 1 });
                                } else {
                                  setNewItem({ ...newItem, selected_units: newItem.selected_units.filter(u => u.id !== unit.id), quantity: Math.max(1, newItem.selected_units.length - 1) });
                                }
                              }}
                            />
                            <div className="flex flex-col">
                              <div className="flex items-center gap-1">
                                <span className="text-xs font-mono">{unit.serial_number}</span>
                                {(unit.serial_number === 'NA' || unit.serial_number === 'N/A') && (
                                  <AlertTriangle className="h-3 w-3 text-amber-500" title="Sin número de serie" />
                                )}
                              </div>
                              {unit.receptor_number && (
                                <span className="text-[10px] text-muted-foreground">{unit.receptor_number}</span>
                              )}
                            </div>
                          </label>
                        ))}
                        {filteredAvailableEquipment.find(e => e.equipment_id === newItem.equipment_id)?.units
                          ?.filter(unit => {
                            if (reservedUnitSerials.has(unit.serial_number)) return false;
                            if (!newItem.serial_search) return true;
                            const q = newItem.serial_search.toLowerCase();
                            const matchSerial = searchBySerial && unit.serial_number?.toLowerCase().includes(q);
                            const matchReceptor = searchByReceptor && unit.receptor_number?.toLowerCase().includes(q);
                            return matchSerial || matchReceptor || (!searchBySerial && !searchByReceptor);
                          }).length === 0 && (
                          <p className="text-xs text-muted-foreground col-span-2 text-center py-2">No se encontraron unidades disponibles</p>
                        )}
                      </div>
                    </div>
                    {newItem.selected_units.length > 0 && (
                      <p className="text-xs text-muted-foreground">{newItem.selected_units.length} unidad(es) seleccionada(s)</p>
                    )}
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Cantidad *</Label>
                    <Input
                      type="number"
                      min="1"
                      max={filteredAvailableEquipment.find(e => e.equipment_id === newItem.equipment_id)?.available_count || 1}
                      value={newItem.quantity === 0 ? '' : newItem.quantity}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || val === '0') {
                          setNewItem({ ...newItem, quantity: 0 });
                        } else {
                          const num = parseInt(val);
                          if (!isNaN(num) && num > 0) {
                            setNewItem({ ...newItem, quantity: num });
                          }
                        }
                      }}
                      onBlur={() => {
                        if (newItem.quantity < 1) setNewItem({ ...newItem, quantity: 1 });
                      }}
                      placeholder="Cantidad"
                      data-testid="quantity-input"
                      disabled={newItem.selected_units.length > 0}
                    />
                    {newItem.equipment_id && (
                      <p className="text-xs text-muted-foreground">
                        Máx disponible: {filteredAvailableEquipment.find(e => e.equipment_id === newItem.equipment_id)?.available_count || 0}
                      </p>
                    )}
                  </div>
                  <div className="flex items-end">
                    <Button type="button" onClick={addItem} variant="secondary" className="w-full" data-testid="add-item-btn">
                      Agregar Equipo
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Items List */}
            {form.items.length > 0 && (
              <div className="space-y-2">
                <Label>Equipos a entregar ({form.items.length})</Label>
                <div className="border rounded-md divide-y max-h-48 overflow-y-auto">
                  {form.items.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3">
                      <div className="flex items-center gap-3">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium">{item.equipment_name}</p>
                            <Badge className="text-xs bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                              Disponible
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Cantidad: {item.quantity} | Origen: {item.location_name || 'N/A'}
                          </p>
                          {item.receptor_numbers && (
                            <p className="text-xs text-muted-foreground font-mono">
                              No. Equipo: {item.receptor_numbers}
                            </p>
                          )}
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeItem(index)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Equipment State Check */}
            {form.items.length > 0 && (
              <div className="space-y-3">
                <div className={`p-4 rounded-lg border transition-all ${form.equipment_state_checked ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-muted/30 border-border/50'}`}>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="equipment_state_checked"
                      checked={form.equipment_state_checked}
                      onCheckedChange={(checked) => setForm({ ...form, equipment_state_checked: checked, equipment_no_review: false })}
                      className="mt-0.5"
                      disabled={form.equipment_no_review}
                    />
                    <div>
                      <Label htmlFor="equipment_state_checked" className="font-medium cursor-pointer flex items-center gap-2">
                        <CheckCircle className={`h-4 w-4 ${form.equipment_state_checked ? 'text-emerald-500' : 'text-muted-foreground'}`} />
                        Confirmo que revisé el estado físico del equipo
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        He verificado que todos los equipos están en buen estado antes de la salida.
                      </p>
                    </div>
                  </div>
                </div>
                <div className={`p-4 rounded-lg border transition-all ${form.equipment_no_review ? 'bg-red-500/10 border-red-500/30' : 'bg-muted/30 border-border/50'}`}>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="equipment_no_review"
                      checked={form.equipment_no_review}
                      onCheckedChange={(checked) => setForm({ ...form, equipment_no_review: checked, equipment_state_checked: false })}
                      className="mt-0.5"
                      disabled={form.equipment_state_checked}
                    />
                    <div>
                      <Label htmlFor="equipment_no_review" className="font-medium cursor-pointer flex items-center gap-2">
                        <AlertTriangle className={`h-4 w-4 ${form.equipment_no_review ? 'text-red-500' : 'text-muted-foreground'}`} />
                        El equipo sale SIN revisión (urgencia/bomberazo)
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Marcar solo en casos de emergencia. Se registrará que el equipo no fue revisado.
                      </p>
                    </div>
                  </div>
                </div>
                <div className={`p-4 rounded-lg border transition-all ${form.include_qr ? 'bg-sky-500/10 border-sky-500/30' : 'bg-muted/30 border-border/50'}`}>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="include_qr"
                      data-testid="include-qr-checkbox"
                      checked={form.include_qr}
                      onCheckedChange={(checked) => setForm({ ...form, include_qr: checked })}
                      className="mt-0.5"
                    />
                    <div>
                      <Label htmlFor="include_qr" className="font-medium cursor-pointer flex items-center gap-2">
                        <QrCode className={`h-4 w-4 ${form.include_qr ? 'text-sky-500' : 'text-muted-foreground'}`} />
                        Incluir código QR para firma digital del cliente
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        El PDF de responsiva incluirá un QR; el coordinador del cliente puede escanearlo con su celular para firmar digitalmente desde sitio.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving || (form.items.length > 0 && !form.equipment_state_checked && !form.equipment_no_review)} 
              className={isDuplicate ? 'bg-amber-600 hover:bg-amber-700' : (form.movement_subtype === 'prestamo' ? 'bg-purple-600 hover:bg-purple-700' : 'btn-primary')}
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {form.equipment_no_review && <AlertTriangle className="h-4 w-4 mr-2 text-red-300" />}
              {isDuplicate && <Copy className="h-4 w-4 mr-2" />}
              {isDuplicate ? 'Confirmar Duplicado' : (form.movement_subtype === 'prestamo' ? 'Registrar Préstamo' : 'Registrar Salida')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
};

export default CreateMovementModal;
